import {Injectable, Injector, Renderer2, TemplateRef, ViewContainerRef} from '@angular/core';

import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment';

import {ColumnCompareFunctionFactory, ColumnConfig, FieldZone, NestedHeaderRow, PreviewInput} from './reports.interfaces';
import {ReportsService} from './reports.service';

import {ComponentLoaderFactory} from 'ngx-bootstrap/component-loader';
import {PositioningService} from 'ngx-bootstrap/positioning';
import {DATE_MAX} from '../app.constants';
import {FieldType} from '../shared/form-generator/fields/fields.interfaces';
import {LocalizationService} from '../shared/localization/localization.service';
import {GPTranslateService} from '../shared/localization/translate.service';
import {DateLocalizationPipe} from '../shared/pipes/date-localization.pipe';
import {PopoverService} from '../shared/ui/popover.service';
import {isValid} from '../shared/utils/date';

export const translateTableData = (data: any[], callback: any) => data.map((element: any) => {
  Object.keys(element).forEach((key: string) => {
    if (Array.isArray(element[key])) {
      const valueArray = element[key];
      element[key] = valueArray.map((value: string) => callback(key, value));
    } else {
      element[key] = callback(key, element[key]);
    }
  });
  return element;
});

export const isNumberType = (type): boolean => {
  if (!type) {
    return false;
  }
  return ([FieldType.INTEGER, FieldType.DECIMAL, FieldType.CURRENCY, FieldType.PERCENTAGE, FieldType.ABSENCE, FieldType.PAY_VALUE]
    .includes(type));
};

export const includesNumberType = (types: FieldType[]): boolean => {
  if (!types || !types.length) {
    return false;
  }
  return types.some(type =>
    [FieldType.INTEGER, FieldType.DECIMAL, FieldType.CURRENCY, FieldType.PERCENTAGE, FieldType.ABSENCE, FieldType.PAY_VALUE]
      .includes(type));
};

export const includesNumberTypeForSum = (types: FieldType[]): boolean => {
  if (!types || !types.length) {
    return false;
  }
  return types.some(type =>
    [FieldType.INTEGER, FieldType.DECIMAL, FieldType.CURRENCY]
      .includes(type));
};

export const isTranslatableType = (type): boolean => {
  if (!type) {
    return false;
  }
  return ([FieldType.LIST, FieldType.TREE, FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED].includes(type));
};

export const includesTranslatableTypes = (types: FieldType[]): boolean => {
  if (!types || !types.length) {
    return false;
  }
  return types.some(type => [FieldType.LIST, FieldType.TREE,
    FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED].includes(type));
};

const FIRST_BEFORE_SECOND = -1;
const FIRST_AFTER_SECOND = 1;
const DO_NOT_SWAP = 0;

const isEmptyCell = (value: unknown): boolean => value === null || value === undefined || value === '';

const booleanRank = (value: unknown): number => value === false || value === 'false' ? 0 : 1;

/**
 * Handsontable's text comparator turns true/false into NaN, so Boolean columns never reorder without this one.
 */
export const booleanCompareFunctionFactory: ColumnCompareFunctionFactory = (sortOrder, _columnMeta, columnPluginSettings) => {
  const isAscending = sortOrder === 'asc';
  const emptyCellsLeadWhenAscending = !!columnPluginSettings?.sortEmptyCells && isAscending;

  return (value: unknown, nextValue: unknown): number => {
    if (isEmptyCell(value) && isEmptyCell(nextValue)) {
      return DO_NOT_SWAP;
    }
    if (isEmptyCell(value)) {
      return emptyCellsLeadWhenAscending ? FIRST_BEFORE_SECOND : FIRST_AFTER_SECOND;
    }
    if (isEmptyCell(nextValue)) {
      return emptyCellsLeadWhenAscending ? FIRST_AFTER_SECOND : FIRST_BEFORE_SECOND;
    }
    const rankDifference = booleanRank(value) - booleanRank(nextValue);
    return isAscending ? rankDifference : -rankDifference;
  };
};

@Injectable()
export class Preview {
  private renderer: Renderer2;
  private viewContainerRef: ViewContainerRef;
  private componentLoaderFactory: ComponentLoaderFactory;
  private positioningService: PositioningService;
  private fileDetailPopover: TemplateRef<unknown>;

  constructor(private injector: Injector,
              private gpTranslateService: GPTranslateService,
              private datePipe: DateLocalizationPipe,
              private localization: LocalizationService,
              private popoverService: PopoverService) {
    this.componentLoaderFactory = this.injector.get(ComponentLoaderFactory);
    this.positioningService = this.injector.get(PositioningService);
  }

  public setRenderer(renderer: Renderer2) {
    this.renderer = renderer;
  }

  public setViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.viewContainerRef = viewContainerRef;
  }

  public setDetailsPopoverContent(fileDetailPopover: TemplateRef<unknown>) {
    this.fileDetailPopover = fileDetailPopover;
  }

  public generatePreview(data: PreviewInput, height: number, zonesMap: Map<string, FieldZone>, language: string): Observable<any> {
    if (!Object.keys(data).length || !data.result.length) {
      return of(null);
    }

    Object.keys(data.position).forEach((parentKey: string) => {
      if (typeof data.position[parentKey] === 'object') {
        Object.keys(data.position[parentKey]).forEach((key: string) => {
          data.position[`${parentKey}:${key}`] = data.position[parentKey][key];
        });

        delete data.position[parentKey];
      } else if (parentKey === 'calculationDate' && !data.result.some(doc => doc['external'])) {
        delete data.position[parentKey];
      }
    });

    const sortedPositions = Object.keys(data.position).sort((a, b) => {
      return data.position[a] - data.position[b];
    });
    const displayHeaders = sortedPositions
      .map(column => `${this.getZoneFirstTranslation(zonesMap.get(column), column)}`);
    const tooltipHeaders = sortedPositions
      .map(column => `${this.getZoneFullTranslation(zonesMap.get(column), column)}`);
    const columns = sortedPositions.map(column => {
      const result: ColumnConfig = {
        data: column,
        type: 'text',
        readOnly: true,
        renderer: ReportsService.readOnlyRenderer
      };
      const zone: FieldZone = zonesMap.get(column);

      if ((zone?.types.includes('DATE')) || column === 'beginDate' || column === 'endDate') {
        result.type = 'date';
        result.dateFormat = this.localization.dateFormat;
      } else if (zone?.types.includes('FILE') || zone?.types.includes('FILES')) {
        result.type = 'text';
        result.isFile = true;
        result.renderer = ReportsService.fileDetailButtonRenderer.bind(this, this.renderer,
          this.viewContainerRef, this.componentLoaderFactory,
          this.positioningService, this.fileDetailPopover, this.popoverService, this.gpTranslateService.instant('EMPLOYEE_MANAGE.details'));
      } else if (zone && includesNumberTypeForSum(zone.types.map(type => FieldType[type]))) {
        result.type = 'numeric';
        result.renderer = ReportsService.readOnlyNumberRenderer.bind(this, this.localization);
      } else if (zone?.types.includes('BOOLEAN')) {
        result.columnSorting = {compareFunctionFactory: booleanCompareFunctionFactory};
      }
      return result;
    });

    return this.generateTableData(data.result, zonesMap, language).pipe(
      map((translateData: Record<string, unknown>[]) => {
        const fullNestedHeaders: NestedHeaderRow[] | null = this.getFullNestedHeaders(sortedPositions, zonesMap);
        const firstNestedHeaders: NestedHeaderRow[] | null = this.getFirstNestedHeaders(sortedPositions, zonesMap);
        const resolvedDisplayHeaders: string[] = firstNestedHeaders
          ? this.concatNestedHeaders(firstNestedHeaders)
          : displayHeaders;
        const resolvedTooltipHeaders: string[] = fullNestedHeaders
          ? this.concatNestedHeaders(fullNestedHeaders)
          : tooltipHeaders;

        const tableData: any = {
          columns: columns,
          options: {
            stretchH: 'all',
            autoWrapRow: true,
            height,
            columnSorting: false,
            sortIndicator: true,
            autoColumnSize: true,
            manualRowResize: true,
            manualColumnResize: true,
            // dropdownMenu: true,
            // filters: true,
            className: 'htCenter htMiddle',
          },
          data: translateData,
          headers: resolvedDisplayHeaders,
          tooltipHeaders: resolvedTooltipHeaders
        };

        if (fullNestedHeaders) {
          tableData.nestedHeaders = firstNestedHeaders;
        }

        return tableData;
      })
    );
  }


  public getFullNestedHeaders(data, zoneMap: Map<string, FieldZone>): NestedHeaderRow[] | null {
    return this.buildNestedHeaders(data, zoneMap,
      (zone: FieldZone, key: string) => this.getZoneFullTranslation(zone, key));
  }

  public getFirstNestedHeaders(data, zoneMap: Map<string, FieldZone>): NestedHeaderRow[] | null {
    return this.buildNestedHeaders(data, zoneMap,
      (zone: FieldZone, key: string) => this.getZoneFirstTranslation(zone, key));
  }

  // TODO Think about a better algorithm
  private buildNestedHeaders(data, zoneMap: Map<string, FieldZone>,
                             translateFn: (zone: FieldZone, key: string) => string): NestedHeaderRow[] | null {
    const firstRow: Map<string, any> = new Map<string, any>();
    const secondRow: any[] = [];
    let hasValues: boolean = false;

    data.forEach((element: string) => {
      const splitValues: string[] = element.split(':');

      if (splitValues.length > 1) {
        if (!firstRow.get(splitValues[0])) {
          firstRow.set(splitValues[0], {
            label: translateFn(zoneMap.get(splitValues[0]), splitValues[0]),
            colspan: 1
          });
        } else {
          const parentValue = firstRow.get(splitValues[0]);
          ++parentValue.colspan;
          firstRow.set(splitValues[0], parentValue);
        }
        secondRow.push(translateFn(zoneMap.get(splitValues[1]), splitValues[1]));
        hasValues = true;
      } else {
        secondRow.push('');
        firstRow.set(element + 'empty', translateFn(zoneMap.get(element), element));
      }
    });

    return hasValues
      ? [
        Array.from(firstRow.values()),
        secondRow
      ]
      : null;
  }

  private concatNestedHeaders(nestedHeaders: Array<Array<any>>) {
    return nestedHeaders.reduce((row1, row2) => row1.map((col, i) => {
      const val1 = typeof (row1[i]) === 'string' ? row1[i] : row1[i].label;
      const val2 = typeof (row2[i]) === 'string' ? row2[i] : row2[i].label;
      return val1 + (val2 !== '' ? ':' + val2 : val2);
    }, []));
  }

  private generateTableData(data: any[], zoneMap: Map<string, FieldZone>, language: string): Observable<any> {
    data.map((parent: any) => {
      Object.keys(parent).forEach((parentKey: string) => {
        if (typeof parent[parentKey] === 'object' && !Array.isArray(parent[parentKey])) {
          Object.keys(parent[parentKey]).forEach((key: string) => {
            parent[`${parentKey}:${key}`] = parent[parentKey][key];
          });

          delete parent[parentKey];
        }
      });
      return parent;
    });

    return of(translateTableData(data,
      (key: string, value: string) => this.getFormatValue(key, value, zoneMap.get(key), this.gpTranslateService.instant(value))));
  }

  private getFormatValue(key: string, value: string, zone: any, translatedValue: string): string {
    if ((key === 'beginDate' || key === 'endDate') && isValid(value, 'YYYY-MM-DD')) {
      return this.datePipe.transform(value);
    }
    if (!zone) {
      return value;
    }
    if (zone.types.includes('DATE') && isValid(value, 'YYYY-MM-DD')) {
      if (moment(value).isSame(DATE_MAX)) {
        return '';
      }
      return this.datePipe.transform(value);
    } else if (includesTranslatableTypes(zone.types.map(type => FieldType[type]))) {
      return translatedValue;
    }
    return value;
  }

  private getZoneFullTranslation(zone: FieldZone | undefined, key: string): string {
    return zone ? this.gpTranslateService.getDisplayTranslation(zone.transIds, ' | ') : this.gpTranslateService.instant(key);
  }

  private getZoneFirstTranslation(zone: FieldZone | undefined, key: string): string {
    return zone ? this.gpTranslateService.getFirstTranslation(zone.transIds) : this.gpTranslateService.instant(key);
  }
}
